/**
 * Tiny Android Studio-style logcat filter expression DSL.
 *
 * Supported tokens (whitespace separated, default AND-combined; flip the
 * combine mode to `'or'` via `matchLine` 第三参数):
 *
 *   tag:Foo            tag contains Foo (case-insensitive substring match)
 *   tag:Foo|Bar        tag contains Foo OR contains Bar (AS-style `|` split)
 *   tag~:Foo|Bar       tag matches regex Foo|Bar
 *   tag=Foo            tag exactly equals Foo (case-sensitive)
 *   tag=Foo|Bar        tag equals Foo OR equals Bar (`|` split applies to `=` too)
 *   tag:"foo|bar"      quoted value preserves `|` as literal (no split)
 *   message:hello      message contains "hello" (case-insensitive)
 *   msg:hello|world    message contains hello OR world
 *   msg~:re            message matches regex
 *   package:com.x      pid belongs to a package whose name contains com.x
 *                      (resolved out-of-band by the caller via a pkg→pids map)
 *   pkg~:^com\.x$      regex variant
 *   pid:1234           pid equals 1234
 *   pid:1234|5678      pid 1234 OR 5678
 *   tid:5678           tid equals 5678
 *   level:W            minimum level is W (Warn / Error / Fatal / Assert)
 *   level=I            level exactly equals I
 *   foo                free text → raw line contains "foo"
 *   foo|bar            free text → raw line contains foo OR bar
 *   "hello world"      free text with spaces (preserved, `|` literal)
 *   -tag:Verbose       NOT tag:Verbose
 *
 * Tokens you don't recognize are treated as free text. Empty expression
 * matches everything.
 *
 * 与 Android Studio 的 logcat 习惯一致：tag / message / pid / tid / package
 * / free-text 的 `:` 和 `=` 都把未加引号的 `|` 视为 OR 分隔；要匹配字面 `|`
 * 请用引号包裹（`tag:"a|b"`）或改用 `~:` 正则模式。
 */
import type { LogLine, LogLevel } from './parser';
import { LEVEL_ORDER } from './parser';

export type FilterField =
  | 'tag'
  | 'message'
  | 'package'
  | 'pid'
  | 'tid'
  | 'level'
  | 'free';

export type FilterOp = 'contains' | 'equals' | 'regex' | 'gte';

export interface FilterTerm {
  field: FilterField;
  op: FilterOp;
  /** Original right-hand side string, used for display & re-serialisation. */
  rhs: string;
  /** Compiled regex when `op === 'regex'`. */
  regex?: RegExp;
  /** Lowercased value for fast contains comparisons. */
  rhsLower?: string;
  /**
   * AS-style 多值匹配：当用户写 `tag:Foo|Bar` 时，`Foo` / `Bar` 落在这里，
   * 任一命中即视为该 term 成立。`contains` 走 `rhsLowerAlts`，`equals`
   * 走 `rhsAlts`。引号包裹的值（`tag:"a|b"`）或单值时为空。
   */
  rhsAlts?: string[];
  rhsLowerAlts?: string[];
  /** Numeric level threshold for `level:` / `level>=`. */
  levelMin?: number;
  negate: boolean;
}

export interface ParseResult {
  terms: FilterTerm[];
  /** Errors collected while compiling (e.g. invalid regex). UI shows them. */
  errors: string[];
}

/* -------------------------------------------------------------------- */
/* Tokenizer                                                            */
/* -------------------------------------------------------------------- */

/**
 * Token + 它在 expr 里的字节区间，方便 chip UI 做"删这一段"或者
 * `inputRef.setSelectionRange(start, end)` 高亮。`end` 不含。
 */
export interface TokenRange {
  token: string;
  start: number;
  end: number;
}

/**
 * Split a filter expression into tokens (with positions), respecting
 * double-quoted strings.
 *  `tag:"hello world" foo` →
 *    [{token:'tag:"hello world"',start:0,end:18},{token:'foo',start:19,end:22}]
 */
export function tokenizeWithRanges(expr: string): TokenRange[] {
  const out: TokenRange[] = [];
  let i = 0;
  const s = expr;
  while (i < s.length) {
    while (i < s.length && /\s/.test(s[i])) i++;
    if (i >= s.length) break;
    const start = i;
    let inQuote = false;
    while (i < s.length) {
      const ch = s[i];
      if (ch === '"') {
        inQuote = !inQuote;
        i++;
        continue;
      }
      if (!inQuote && /\s/.test(ch)) break;
      i++;
    }
    out.push({ token: s.slice(start, i), start, end: i });
  }
  return out;
}

/**
 * Split a filter expression into tokens, respecting double-quoted strings.
 *  `tag:"hello world" foo` → ['tag:"hello world"', 'foo']
 */
export function tokenize(expr: string): string[] {
  return tokenizeWithRanges(expr).map((r) => r.token);
}

/* -------------------------------------------------------------------- */
/* Parser                                                               */
/* -------------------------------------------------------------------- */

const FIELD_ALIASES: Record<string, FilterField> = {
  tag: 'tag',
  t: 'tag',
  message: 'message',
  msg: 'message',
  m: 'message',
  pkg: 'package',
  package: 'package',
  pid: 'pid',
  tid: 'tid',
  level: 'level',
  lv: 'level',
  l: 'level',
};

const LEVEL_ALIASES: Record<string, LogLevel> = {
  V: 'V', VERBOSE: 'V',
  D: 'D', DEBUG: 'D',
  I: 'I', INFO: 'I',
  W: 'W', WARN: 'W', WARNING: 'W',
  E: 'E', ERROR: 'E',
  F: 'F', FATAL: 'F',
  A: 'A', ASSERT: 'A',
};

function isQuoted(v: string): boolean {
  return v.length >= 2 && v.startsWith('"') && v.endsWith('"');
}

function unquote(v: string): string {
  if (isQuoted(v)) return v.slice(1, -1);
  return v;
}

/**
 * 按"未加引号"的 `|` 把 value 拆成 OR 分支并去掉空白与空段。
 *
 * 引号语义有两层：
 *   - 整个 value 被外层引号包裹（`tag:"a|b"`）：调用方先用 `isQuoted`
 *     检查，命中就别调本函数，整体当字面量。
 *   - **多 alt 中某个 alt 被引号包裹**（`tag:A|"foo bar"|B`）：本函数
 *     按 quote-aware 扫描，引号内的 `|` 不当分隔符，分到该 alt 后再
 *     `unquote`。这样 chip toggle 把含空格的 tag 写成 `tag:A|"foo bar"`
 *     时仍然能正确解析。
 *
 * 返回所有非空分支；调用方根据 length 决定怎么用：
 *   - 0：value 只剩 `|` / `||`（退化），调用方 fallback 到原始 value；
 *   - 1：单分支（用户写 `tag:my|` 这种），调用方用 alts[0] 作 single-value
 *     match（这样 `tag:my|` 等价 `tag:my`，不会因为残留 `|` 让 contains
 *     永远不命中）；
 *   - ≥2：真正的 OR-多值，挂到 term 的 rhsAlts / rhsLowerAlts。
 */
function splitPipeAlts(value: string): string[] {
  if (!value.includes('|')) return [];
  return splitAltsAlways(value);
}

/**
 * 跟 `splitPipeAlts` 一样的 quote-aware 切分，但单值（无 `|`）也会返回
 * `[value]`。`toggleTagInExpr` 那一类编辑工具想知道"现有 token 里挂着哪
 * 些 alt"，单值时也算一个 alt。`splitPipeAlts` 那一套语义被 parser 内部
 * 依赖（无 `|` 走 `rhs` 单值快路径），不要混用。
 */
function splitAltsAlways(value: string): string[] {
  const parts: string[] = [];
  let cur = '';
  let inQuote = false;
  for (let i = 0; i < value.length; i++) {
    const ch = value[i];
    if (ch === '"') {
      inQuote = !inQuote;
      cur += ch;
      continue;
    }
    if (!inQuote && ch === '|') {
      parts.push(cur);
      cur = '';
      continue;
    }
    cur += ch;
  }
  parts.push(cur);
  return parts
    .map((p) => p.trim())
    .map((p) => (isQuoted(p) ? unquote(p) : p))
    .filter((p) => p.length > 0);
}

/**
 * AS-style "正在输入中" 探测：未加引号的 value 含 `|` 且有任意空 alt
 * （`aa|`、`|aa`、`aa||bb`），说明用户正在敲下一个备选值，过滤器应当
 * **暂不收紧**——AS 在这种半成品状态下表现为"不过滤这一项，展示全部
 * 日志"，方便用户边看边挑下一个值（典型场景：打开 tag 智能提示挑下
 * 一个 tag）。
 *
 * 引号内的 `|` 是字面量（如 `tag:"a|b"`），不算半成品。
 */
function hasIncompletePipeAlt(value: string): boolean {
  if (!value.includes('|')) return false;
  for (const part of value.split('|')) {
    if (part.trim().length === 0) return true;
  }
  return false;
}

function compileTerm(token: string, errors: string[]): FilterTerm | null {
  let body = token;
  let negate = false;
  if (body.startsWith('-') && body.length > 1) {
    negate = true;
    body = body.slice(1);
  }

  // Match `key<op>value` where op is one of `~:`, `=`, `:`, `>=`, `<=`.
  const opMatch = /^([A-Za-z_]+)(~:|>=|<=|=|:)([\s\S]*)$/.exec(body);
  if (opMatch) {
    const rawKey = opMatch[1].toLowerCase();
    const op = opMatch[2];
    const rawValue = opMatch[3];
    const wasQuoted = isQuoted(rawValue);
    const value = unquote(rawValue);
    const field = FIELD_ALIASES[rawKey];
    if (field) {
      return buildFieldTerm(field, op, value, wasQuoted, negate, errors);
    }
  }

  // Free text: substring of raw line.
  const wasQuoted = isQuoted(body);
  const text = unquote(body);
  if (!text) return null;
  // 半成品 `aa|` / `|aa` / `aa||bb`：当前 term 不收紧（用户正在挑下一个
  // alt），返回 null 让上层不挂这条 term。
  if (!wasQuoted && hasIncompletePipeAlt(text)) return null;
  const term: FilterTerm = {
    field: 'free',
    op: 'contains',
    rhs: text,
    rhsLower: text.toLowerCase(),
    negate,
  };
  if (!wasQuoted) {
    const alts = splitPipeAlts(text);
    if (alts.length >= 2) {
      term.rhsAlts = alts;
      term.rhsLowerAlts = alts.map((a) => a.toLowerCase());
    } else if (alts.length === 1) {
      term.rhs = alts[0];
      term.rhsLower = alts[0].toLowerCase();
    }
  }
  return term;
}

function buildFieldTerm(
  field: FilterField,
  op: string,
  value: string,
  wasQuoted: boolean,
  negate: boolean,
  errors: string[]
): FilterTerm | null {
  // 半成品 prefix：用户敲到 `tag:` / `msg:` / `pid:` / `tag~:` / `tag=`
  // 等字段名 + 操作符但 value 还没填，按 AS 习惯不收紧过滤器（语义同
  // `hasIncompletePipeAlt` 对应的 trailing-pipe 半成品；只是把"完全为
  // 空"这条更基础的半成品形态补上）。`wasQuoted` 例外：`tag:""` 是用户
  // 主动写的空字面量，仍按 equals/contains 空串处理 —— 极冷门但语义
  // 上"用户明确表达想找 tag 为空的行"，不该静默吞掉。
  if (value.length === 0 && !wasQuoted) return null;

  const term: FilterTerm = {
    field,
    op: 'contains',
    rhs: value,
    rhsLower: value.toLowerCase(),
    negate,
  };

  if (field === 'level') {
    const lv = LEVEL_ALIASES[value.toUpperCase()];
    if (!lv) {
      errors.push(`未知 level：${value}`);
      return null;
    }
    // `:` and `>=` mean ≥ (Android Studio convention); `=` is strict equality.
    if (op === '=') term.op = 'equals';
    else term.op = 'gte';
    term.rhs = lv;
    term.levelMin = LEVEL_ORDER[lv];
    return term;
  }

  if (op === '~:') {
    try {
      term.regex = new RegExp(value, 'i');
      term.op = 'regex';
    } catch (e) {
      errors.push(`非法正则：${value}`);
      return null;
    }
    return term;
  }

  if (op === '=') term.op = 'equals';
  else term.op = 'contains';

  if (!wasQuoted) {
    // 半成品 `tag:aa|`：用户正在挑下一个 alt，不收紧 → 丢弃 term。
    // 配合智能提示让用户能继续在全量日志里挑。
    if (hasIncompletePipeAlt(value)) return null;
    const alts = splitPipeAlts(value);
    if (alts.length >= 2) {
      term.rhsAlts = alts;
      term.rhsLowerAlts = alts.map((a) => a.toLowerCase());
    } else if (alts.length === 1) {
      term.rhs = alts[0];
      term.rhsLower = alts[0].toLowerCase();
    }
  }
  return term;
}

export function parseFilter(expr: string): ParseResult {
  const tokens = tokenize(expr);
  const errors: string[] = [];
  const terms: FilterTerm[] = [];
  for (const t of tokens) {
    const term = compileTerm(t, errors);
    if (term) terms.push(term);
  }
  return { terms, errors };
}

/* -------------------------------------------------------------------- */
/* Expression edit helpers                                              */
/* -------------------------------------------------------------------- */

/** 匹配 `tag:` / `t:` 的"包含集合" token；不带 `-` 前缀，op 必须是 `:`。 */
const TAG_INCLUDE_RE = /^(tag|t):([\s\S]*)$/i;
/** 匹配 `-tag:` / `-t:` 的"排除集合" token；同样限定 `:` op。 */
const TAG_EXCLUDE_RE = /^-(tag|t):([\s\S]*)$/i;

/**
 * 一个候选 alt 是否需要被引号包裹才能放进 `tag:A|B|...` 的多 alt 序列。
 * 触发条件：含空白 / `|` / `"`，或以 `-` 起头（误识别为 negate）。
 * `:` 与 `=` 在 value 内部其实不影响（前缀已被消耗），但谨慎包裹避免歧义。
 */
function altNeedsQuote(v: string): boolean {
  return /\s|[|":=]/.test(v) || v.startsWith('-');
}

function quoteAlt(v: string): string {
  if (!altNeedsQuote(v)) return v;
  // DSL 不支持 escape，把内嵌 `"` 简单去掉（极端 corner，正常 tag/pid 不
  // 会含 `"`）。
  return `"${v.replace(/"/g, '')}"`;
}

function emitTagToken(prefix: '' | '-', alts: string[]): string {
  return `${prefix}tag:${alts.map(quoteAlt).join('|')}`;
}

/**
 * 把 `value` 在 `expr` 的 tag 包含/排除集合中 toggle：
 *   - mode=`'include'`：操作不带 `-` 前缀的 `tag:` token。
 *   - mode=`'exclude'`：操作 `-tag:` token。
 *
 * 行为：
 *   - 找到所有目标 token（忽略 `tag=` / `tag~:` / 整体被引号包裹的字面值
 *     `tag:"a|b"`——这些是用户特意写的精确/正则/字面，工具不去碰）。
 *   - 把它们的 alts 集中起来，用按出现顺序去重的 list 当 set。
 *   - value 在 set 里 → 移除；不在 → 追加。
 *   - 重新拼回 expr：合并到第一个目标 token，其它清空（连带相邻空格）。
 *   - 集合空 → 整段 token 抹掉。
 *   - 没有任何目标 token 时（且要 toggle in），在 expr 末尾追加一个新
 *     `tag:value` / `-tag:value`。
 *
 * 大小写：alt 之间按 case-sensitive 完全相等去重。logcat 行里的 tag 是
 * 已知确切串，用户 click 进来的 value 跟现有 alt 比较一律按字面相等；
 * 不试图理解"被现有 substring 覆盖"。
 */
export function toggleTagInExpr(
  expr: string,
  value: string,
  mode: 'include' | 'exclude' = 'include',
): string {
  if (value.length === 0) return expr;

  const ranges = tokenizeWithRanges(expr);
  type Slot = { range: TokenRange; alts: string[] };
  const slots: Slot[] = [];

  for (const r of ranges) {
    const tk = r.token;
    if (mode === 'include') {
      if (tk.startsWith('-')) continue;
      const m = TAG_INCLUDE_RE.exec(tk);
      if (!m) continue;
      const rawValue = m[2];
      // `tag:"a|b"`：用户主动写引号 = 字面，不动它
      if (isQuoted(rawValue)) continue;
      slots.push({ range: r, alts: splitAltsAlways(rawValue) });
    } else {
      if (!tk.startsWith('-')) continue;
      const m = TAG_EXCLUDE_RE.exec(tk);
      if (!m) continue;
      const rawValue = m[2];
      if (isQuoted(rawValue)) continue;
      slots.push({ range: r, alts: splitAltsAlways(rawValue) });
    }
  }

  // 没有任何匹配 slot：直接 append 一个新 token（toggle out 当 in 处理，
  // 因为没有现存 alt 可移除——退化成新增；UI 端实际不会触发，因为前端
  // 通常仅对"已知存在"的 alt 调用 exclude toggle）。
  if (slots.length === 0) {
    const prefix = mode === 'exclude' ? '-' : '';
    const tok = emitTagToken(prefix, [value]);
    if (expr.length === 0) return tok;
    return `${expr.replace(/\s+$/, '')} ${tok}`;
  }

  // 合并所有 slot 的 alt → 顺序去重 list。
  const seen = new Set<string>();
  const merged: string[] = [];
  for (const s of slots) {
    for (const a of s.alts) {
      if (!seen.has(a)) {
        seen.add(a);
        merged.push(a);
      }
    }
  }

  let nextAlts: string[];
  if (seen.has(value)) {
    nextAlts = merged.filter((a) => a !== value);
  } else {
    nextAlts = [...merged, value];
  }

  // 第一个 slot 用新 alts 重建（或整段移除）；其它 slot 全部移除。
  // 用 ranges 反向切片以保持 index 稳定。
  const prefix = mode === 'exclude' ? '-' : '';
  const replacements: Array<{ start: number; end: number; text: string }> = [];

  if (nextAlts.length === 0) {
    // 全部 slot 删除 → 留意吞掉相邻的多余空格
    for (const s of slots) replacements.push({ start: s.range.start, end: s.range.end, text: '' });
  } else {
    // 首个 slot 改写成新 token，其它清空
    const first = slots[0];
    replacements.push({
      start: first.range.start,
      end: first.range.end,
      text: emitTagToken(prefix, nextAlts),
    });
    for (let i = 1; i < slots.length; i++) {
      replacements.push({
        start: slots[i].range.start,
        end: slots[i].range.end,
        text: '',
      });
    }
  }

  // 按 start 倒序应用 replacement，保证 index 不偏移
  replacements.sort((a, b) => b.start - a.start);
  let next = expr;
  for (const r of replacements) {
    const before = next.slice(0, r.start);
    const after = next.slice(r.end);
    if (r.text === '') {
      // 吞掉前导/尾随多余空白：优先吞前一个空格，没有就吞后一个
      const leading = /\s$/.test(before);
      const trailing = /^\s/.test(after);
      if (leading) {
        next = before.replace(/\s+$/, '') + after;
      } else if (trailing) {
        next = before + after.replace(/^\s+/, '');
      } else {
        next = before + after;
      }
    } else {
      next = before + r.text + after;
    }
  }
  // 整理：合并多余空白、去掉首尾空白
  return next.replace(/\s{2,}/g, ' ').trim();
}

/* -------------------------------------------------------------------- */
/* Matcher                                                              */
/* -------------------------------------------------------------------- */

export interface MatchCtx {
  /**
   * Optional pid → package map. When the user filters by `package:`, we
   * resolve the line's pid through this map.
   */
  pidPackage?: Record<string, string>;
}

function matchOne(line: LogLine, term: FilterTerm, ctx: MatchCtx): boolean {
  let target = '';
  switch (term.field) {
    case 'tag':
      target = line.tag;
      break;
    case 'message':
      target = line.message;
      break;
    case 'pid':
      target = line.pid;
      break;
    case 'tid':
      target = line.tid;
      break;
    case 'package':
      target = ctx.pidPackage?.[line.pid] ?? '';
      break;
    case 'level': {
      const lhs = line.level === '?' ? -1 : LEVEL_ORDER[line.level];
      const rhs = term.levelMin ?? -1;
      if (term.op === 'equals') return lhs === rhs;
      // gte: line level >= filter
      return lhs >= rhs;
    }
    case 'free':
      target = line.raw;
      break;
  }
  switch (term.op) {
    case 'regex':
      return Boolean(term.regex && term.regex.test(target));
    case 'equals':
      if (term.rhsAlts && term.rhsAlts.length > 0) {
        for (const alt of term.rhsAlts) if (target === alt) return true;
        return false;
      }
      return target === term.rhs;
    case 'contains':
    default: {
      const lower = target.toLowerCase();
      if (term.rhsLowerAlts && term.rhsLowerAlts.length > 0) {
        for (const a of term.rhsLowerAlts) if (lower.includes(a)) return true;
        return false;
      }
      if (!term.rhsLower) return false;
      return lower.includes(term.rhsLower);
    }
  }
}

/**
 * 多条 term 之间的合成模式：
 *   - `'and'`（默认，与 AS 一致）：所有 term 都通过 line 才命中。
 *   - `'or'`：任一 term 通过即命中。
 *
 * Negate 含义按"单条 term 自身"判定：`-tag:Foo` 视作"该 term 命中 ⇔
 * 行的 tag 不含 Foo"，OR 模式下任意 term 命中即整体命中。这与用户的
 * 直觉一致——negate 只反转该 term 的 polarity，不影响 combine。
 */
export type CombineMode = 'and' | 'or';

export function matchLine(
  line: LogLine,
  terms: FilterTerm[],
  ctx: MatchCtx = {},
  combine: CombineMode = 'and',
): boolean {
  if (terms.length === 0) return true;
  if (combine === 'or') {
    for (const t of terms) {
      const hit = matchOne(line, t, ctx);
      if (t.negate ? !hit : hit) return true;
    }
    return false;
  }
  for (const t of terms) {
    const hit = matchOne(line, t, ctx);
    if (t.negate ? hit : !hit) return false;
  }
  return true;
}

/**
 * Independent of the expression DSL: cheap level membership filter for the
 * toolbar [V][D][I][W][E] toggles. Empty / null `levels` (or all-on) is a
 * no-op fast path so we don't pay per-line overhead when no toggle is set.
 *
 * Unknown-level lines ('?') are kept unless they're explicitly excluded by
 * passing levels = ['V'] only etc.; we treat '?' as "verbose-ish" and let it
 * through whenever V is enabled. Most adb logcat lines parse cleanly so this
 * is rarely material.
 */
export function lineMatchesLevels(line: LogLine, levels: LogLevel[]): boolean {
  if (levels.length === 0) return true;
  if (line.level === '?') return levels.includes('V');
  return levels.includes(line.level);
}

/**
 * Render a token suggestion for autocomplete. Used by the filter input.
 */
export const FILTER_HINTS: Array<{ text: string; desc: string }> = [
  { text: 'tag:', desc: '标签包含（多值 |：tag:Foo|Bar；末尾 | 表示"正在挑下一个"，暂不过滤）' },
  { text: 'tag~:', desc: '标签匹配正则' },
  { text: 'tag=', desc: '标签精确等于（多值用 |：tag=A|B）' },
  { text: 'message:', desc: '消息包含（多值用 |：msg:foo|bar）' },
  { text: 'msg~:', desc: '消息匹配正则' },
  { text: 'package:', desc: '按包名过滤（需 pid→pkg 映射）' },
  { text: 'pid:', desc: '指定 PID（多值用 |）' },
  { text: 'tid:', desc: '指定 TID（多值用 |）' },
  { text: 'level:W', desc: '最低 Warn 级别' },
  { text: 'level:E', desc: '最低 Error 级别' },
  { text: '-tag:', desc: '反向匹配（NOT）' },
];
